// Active family state — the one the current user is admin/member of.
// Resolved by listing families their email is in. For Phase 1 each user is
// in exactly one family, so we just pick the first.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, type Family, type FamilyMember } from "./api.js";
import { useAuth } from "./auth.js";

interface FamilyState {
  family: Family | null;
  member: FamilyMember | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const FamilyContext = createContext<FamilyState | null>(null);

const FAMILY_ID_KEY = "inkling:family-id";

export function FamilyProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const [family, setFamily] = useState<Family | null>(null);
  const [member, setMember] = useState<FamilyMember | null>(null);
  // `loading` semantics: "we haven't completed a refresh attempt yet". Stays
  // true through the entire AuthProvider load AND the subsequent /me/family
  // fetch — flips to false only after we've attempted to resolve the family
  // (or definitively know there's no session). Protects against a race where
  // FamilyProvider's first useEffect sees `session=null` (still loading)
  // and prematurely flips loading=false, causing Protected to redirect to
  // /onboarding before the family is resolved.
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    if (!session) {
      setFamily(null);
      setMember(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Server-side resolution by session email — no localStorage dependence,
      // works on any device after sign-in.
      const { family, member } = await api.myFamily();
      setFamily(family);
      setMember(member);
      if (family) localStorage.setItem(FAMILY_ID_KEY, family.id);
      else localStorage.removeItem(FAMILY_ID_KEY);
    } catch (err) {
      console.error("[family] refresh failed", err);
      setFamily(null);
      setMember(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Wait for AuthProvider to finish loading before deciding what to do —
    // otherwise we'd take the no-session early return on mount even when a
    // valid session is being fetched.
    if (authLoading) return;
    void refresh();
  }, [session?.email, authLoading]);

  return (
    <FamilyContext.Provider value={{ family, member, loading, refresh }}>
      {children}
    </FamilyContext.Provider>
  );
}

export function useFamily(): FamilyState {
  const ctx = useContext(FamilyContext);
  if (!ctx) throw new Error("useFamily must be used within FamilyProvider");
  return ctx;
}

export function setActiveFamilyId(id: string): void {
  localStorage.setItem(FAMILY_ID_KEY, id);
}

export function clearActiveFamilyId(): void {
  localStorage.removeItem(FAMILY_ID_KEY);
}
